import {
  reduceGameState,
  selectViewerState,
  validateAction,
  type YachtAction,
  type YachtGameState,
  type YachtViewerState,
} from "@cloudnest/yacht-engine";
import { toYachtReplayEvent, type YachtReplayEvent } from "./event-adapter";
import type { YachtReplayFixtureEvent } from "./replay-fixtures.generated";

export interface YachtReplaySnapshot {
  readonly index: number;
  readonly event: YachtReplayFixtureEvent;
  readonly replayEvent: YachtReplayEvent;
  readonly state: YachtGameState;
  readonly viewerState: YachtViewerState;
}

export interface BuildYachtReplayOptions {
  readonly observerUserId?: string;
}

const FORBIDDEN_VIEWER_STATE_MARKERS = [
  "gameSeed",
  "rngState",
  "streamId",
  "mulberry32",
] as const;

function requireState(
  state: YachtGameState | null,
  eventType: YachtReplayEvent["eventType"],
): YachtGameState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function toAction(event: YachtReplayEvent): YachtAction | null {
  switch (event.eventType) {
    case "init":
    case "SyncViewerState":
      return null;
    case "ROLL_DICE":
      return {
        type: "ROLL_DICE",
        playerId: event.userId,
        sheetRound: event.payload.sheetRound,
        turnNumber: event.payload.turnNumber,
      };
    case "TOGGLE_HOLD":
      return {
        type: "TOGGLE_HOLD",
        playerId: event.userId,
        sheetRound: event.payload.sheetRound,
        turnNumber: event.payload.turnNumber,
        dieIndex: event.payload.dieIndex,
      };
    case "SCORE_CATEGORY":
      return {
        type: "SCORE_CATEGORY",
        playerId: event.userId,
        sheetRound: event.payload.sheetRound,
        turnNumber: event.payload.turnNumber,
        category: event.payload.category,
      };
  }
}

function applyReplayEvent(
  state: YachtGameState | null,
  event: YachtReplayEvent,
): YachtGameState {
  if (event.eventType === "init") {
    return event.payload.state;
  }

  const current = requireState(state, event.eventType);
  if (event.eventType === "SyncViewerState") {
    return current;
  }

  const action = toAction(event);
  if (action === null) {
    return current;
  }

  const validation = validateAction(current, action);
  if (!validation.valid) {
    throw new Error(
      `${event.eventType} rejected: ${validation.error ?? "Validation failed"}`,
    );
  }

  const result = reduceGameState(current, action);
  if (!result.valid) {
    throw new Error(`${event.eventType} rejected: ${result.error}`);
  }

  return result.state;
}

function assertObserverSafeViewerState(
  viewerState: YachtViewerState,
  sequenceNumber: number,
): void {
  const serialized = JSON.stringify(viewerState);
  const leakedMarker = FORBIDDEN_VIEWER_STATE_MARKERS.find((marker) =>
    serialized.includes(marker),
  );
  if (leakedMarker) {
    throw new Error(
      `Yacht replay event #${sequenceNumber} leaks ${leakedMarker}`,
    );
  }
}

export function buildYachtReplaySnapshots(
  events: readonly YachtReplayFixtureEvent[],
  options: BuildYachtReplayOptions = {},
): readonly YachtReplaySnapshot[] {
  const observerUserId = options.observerUserId ?? "observer";
  const snapshots: YachtReplaySnapshot[] = [];
  let state: YachtGameState | null = null;

  for (const event of events) {
    const replayEvent = toYachtReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    const viewerState = selectViewerState(state, observerUserId);
    assertObserverSafeViewerState(viewerState, event.sequenceNumber);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      viewerState,
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Yacht event log");
  }

  return snapshots;
}
